/**
 * Provider and Model Code Names Configuration
 *
 * Maps real provider/model names to user-facing code names.
 * This ensures no actual provider names are exposed to users in error messages.
 *
 * Code Name Mapping (official families — keep every cost/wallet/error surface
 * on these; never surface a raw vendor name in user-facing UI):
 * - Perplexity -> Echo (matches model prefix echo-*)
 * - Anthropic/Claude Opus -> Apex; Claude Sonnet (majors >= 5) -> Nova;
 *   Claude Fable (frontier) -> Apogee (minted 2026-07-10)
 * - OpenAI -> Prism (matches model prefix prism-*)
 * - Google/Gemini -> Titan (2026-07-06: unified on Titan — the model-level
 *   name every pricing/wallet surface already used; 'Spark' retired)
 * - x-ai/Grok -> Pulse
 * - z-ai/GLM -> Zenith; DeepSeek -> Fathom; MiniMax -> Maxim;
 *   Moonshot/Kimi -> Lunar; Xiaomi/MiMo -> Mirage; Qwen -> Quill
 *   (minted 2026-07-06 for the tier-2/3/11 ladder vendors that had no
 *   white-label family; sanctioned 2026-07-07.)
 *
 * NAMING RULE (2026-07-07 Miles directive): vendor model DESIGNATIONS must
 * not leak into codenames — e.g. gpt-5.3-codex is 'prism-5.3' (not
 * prism-5.3-codex), kimi-k2.6 is 'lunar-2.6' (not lunar-k2.6), minimax-m3
 * is 'maxim-3'. Generic size/speed descriptors (pro/mini/nano/max/flash)
 * are fine — they don't identify a vendor product.
 */

// SSOT display-time effort fallback (2026-08-16 (Miles) — generated leaf
// module, no import cycle: modelTiers.generated imports nothing).
import { SSOT_MODEL_EFFORT } from './modelTiers.generated';

// ============================================================================
// PUBLIC SYSTEM STATION NAMES (2026-08-13 presentation layer)
// ============================================================================
// User-facing surfaces stopped rendering versioned codenames (apex-4.8,
// prism-5.6-s, motif-1.2): version digits mirror upstream releases closely
// enough for the community to reverse-map them to vendor models. Every UI
// display site now goes through getPublicSystemName()/getPublicSystemNameForUsage(),
// which resolve to versionless "print floor" station names. The versioned
// codename layer below is UNCHANGED and internal-only (pricing resolution,
// persistence round-trips, logs) — it is load-bearing for the four permanent
// pricing maps and the tier SSOT battery. Never render its output directly.
//
// Public display names are assigned per codename FAMILY (one name per model
// line); the scheme is documented in NAMING-SYSTEM-INTERNAL.md.
//
// 2026-08-13 (Miles directive — same-day revert of the print-floor "station"
// vocabulary): the public name IS the internal family word — apex, nova,
// swift, prism, pulse, echo… — the codename flavor the owner standardized on.
// What leaked model identity was never the family word: it was the VERSION
// DIGITS tracking vendor release cadence (and, in the old palette,
// vendor-brand badge colors — already re-cut separately). So the load-bearing
// rules stay: the public string is VERSIONLESS, dictionary words, NO digits
// ever (tests/tools/publicSystemNames.test.ts enforces), and never a vendor
// model designation.
//
// 2026-08-14 (Miles directives, this round):
//  • CASING: all-lowercase everywhere, matching the brand style (prest0, pr0)
//    — "apex", never "Apex".
//  • GENERATIONS ("mint words"): different generations of one family must be
//    tellable apart WITHOUT numbers — there is more than one Claude Opus in
//    the records. Each (family, generation) pair carries a second lowercase
//    word from the closed lexicon in GENERATION_MINT_WORDS below: "apex
//    cedar" (current) vs "apex basalt" (previous). The word order is
//    ARBITRARY BY DESIGN — never alphabetical, never seasonal — so outsiders
//    cannot count generations or infer release cadence from the vocabulary.
//    A new generation = one new entry there; the publicSystemNames test
//    battery fails any MODEL_CODE_NAMES row whose generation lacks a mint
//    word, so a new model cannot ship unnamed.
export const SYSTEM_STATION_NAMES: Record<string, string> = {
  // Anthropic lines (capability-ordered)
  apogee: 'apogee',
  apex: 'apex',
  nova: 'nova',
  swift: 'swift', // legacy Haiku rows
  // OpenAI
  prism: 'prism',
  // Google (spark = retired legacy Gemini family, old records — unified
  // under titan so one model line keeps exactly one public name)
  titan: 'titan',
  spark: 'titan',
  // x-ai / Grok
  pulse: 'pulse',
  // z-ai / GLM
  zenith: 'zenith',
  // DeepSeek
  fathom: 'fathom',
  // MiniMax
  maxim: 'maxim',
  // Moonshot / Kimi
  lunar: 'lunar',
  // Xiaomi / MiMo
  mirage: 'mirage',
  // Qwen
  quill: 'quill',
  // Meta
  motif: 'motif',
  // Perplexity
  echo: 'echo',
  // ── Non-text product vendors (registered 2026-09-02) ──────────────────────
  // The 2026-09-01 re-ladder put image/video/voice/embeddings/captcha vendors
  // on tiers 8/9/10/12/15 without a family word, so every one of those usage
  // rows rendered as bare "system" (and two mis-resolved onto an existing
  // family's generation — see MODEL_CODE_NAMES). Words follow the house rule:
  // one evocative lowercase dictionary word per model LINE, no vendor
  // designation, no digits.
  beacon: 'beacon',   // Black Forest Labs (FLUX image)
  cipher: 'cipher',   // ByteDance (Dreamina Seedance video)
  atlas: 'atlas',     // Alibaba video lines (Wan, HappyHorse) — Qwen keeps quill
  chorus: 'chorus',   // ElevenLabs (voice agents)
  arbor: 'arbor',     // Amazon (Nova Sonic voice) — never the Anthropic 'nova'
  vantage: 'vantage', // Voyage AI (embeddings)
  sentry: 'sentry',   // 2captcha solver endpoints
  harbor: 'harbor',   // fal-hosted third-party audio lines (Tier 22, 2026-09-08)
  reflex: 'reflex',   // TypeSafe System One decision model (Tier 23, 2026-09-28)
  mosaic: 'mosaic',   // Microsoft (MAI-Image, Tier 8, 2026-10-12)
};

// ── Generation mint words (2026-08-14) ──────────────────────────────────────
// Keyed by internal versioned-codename GENERATION PREFIX (longest-prefix
// match against getModelCodeName output). Build variants within one
// generation (prism-5.6-s/-l/-t; -mini/-nano; -pro) share the generation's
// word — variant identity is deliberately collapsed in public. Legacy-only
// families (swift/spark rows) carry no mint word: the bare family word IS
// the complete name. Words are quiet terrain/material nouns with no
// ordering signal; retired generations keep their words FOREVER so
// historical activity rows stay readable.
//
// 2026-08-14 follow-up (Miles): echo is minted PER PRODUCT, not per
// generation — one turn routinely mixes a cheap search call and a deep
// research call, and both rendering as bare "echo" hid a 60× cost spread
// between same-named rows in the ⓘ popup. Bare "echo" now means the base
// search product OR a provider-only row with no model id (the two share an
// internal code and cannot be told apart at this layer).
export const GENERATION_MINT_WORDS: Record<string, string> = {
  // Claude Fable (frontier)
  'apogee-5.1': 'knoll', // 2026-09-01 Fable 5.1 rotation (quiet terrain noun, no ordering signal)
  'apogee-5': 'crest',
  // Claude Opus generations
  'apex-5.5': 'tuff', // 2026-09-27 Opus 5.5 (volcanic rock noun, no ordering signal)
  'apex-5': 'cedar',
  'apex-4.8': 'basalt',
  'apex-4.7': 'flint',
  'apex-4.6': 'shale',
  'apex-4.5': 'marl',
  // Claude Sonnet
  'nova-5.5': 'loam', // 2026-09-28 Sonnet 5.5 (soil noun, no ordering signal): the always-latest Sonnet alias serves it
  'nova-5': 'ember',
  // OpenAI. 2026-08-14 follow-up (Miles): the three 5.6 builds are minted
  // INDIVIDUALLY — they are distinct serving models with distinct behavior
  // and cost, co-appearing in breakdowns. Words are deliberately NOT
  // initial-congruent with the internal single-letter variant tags (an
  // initial-matched word would hand outsiders the decoder the tags exist to
  // prevent). Bare 'prism-5.6' (variant unknown) falls back to the primary
  // build's word, mirroring the bare-echo precedent.
  // 2026-09-04: GPT 6 generation (astra = variant tag 'a', deliberately NOT initial-congruent
  // with the mint word). Bare 'prism-6' falls back to the primary build's word.
  'prism-6-a': 'schist',
  'prism-6': 'schist',
  // 2026-09-30 (tier-4 review): the rest of the GPT-6 generation, each build minted on its own
  // (the 5.6 precedent), none initial-congruent with its variant tag. 6.1 is a point release,
  // so it carries its own generation word; bare 'prism-6.1' falls back to it.
  'prism-6-s': 'beryl',
  'prism-6-l': 'opal',
  'prism-6.1-s': 'garnet',
  'prism-6.1': 'garnet',
  'prism-5.6-s': 'quartz',
  'prism-5.6-l': 'jasper',
  'prism-5.6-t': 'slate',
  'prism-5.6': 'quartz',
  'prism-5.5': 'gypsum',
  'prism-5.4': 'mica',
  'prism-5.3': 'onyx',
  'prism-4': 'agate',
  'prism-4t': 'agate', // turbo build of the same generation
  'prism-3': 'chert',
  // Audio product (2026-08-19, tier-10-voice rungs). Minted PER PRODUCT on the
  // echo precedent: gpt-audio bills $32/$64 per million against gpt-5.6-sol's
  // $5/$30, so leaving both to render as bare "prism" would hide a 6x cost
  // spread between same-named rows in the ⓘ popup. The -mini build shares the
  // product's word (variant identity collapses in public, as with -mini/-nano).
  'prism-audio': 'pumice',
  'prism-realtime': 'scoria', // 2026-09-01 tier-10 re-ladder: realtime product line (1.5 + GA snapshot share it)
  // Google
  'titan-3.8': 'swale', // 2026-09-03: 3.8-flash generation (landform family, no ordering signal)
  'titan-3.7': 'playa', // 2026-08-17: 3.7-flash generation (landform family)
  'titan-3.6': 'dune',
  'titan-3.5': 'mesa',
  'titan-3.1': 'butte',
  'titan-pro': 'butte', // pro-latest alias resolves to the 3.1 generation today
  // x-ai / Grok
  'pulse-4.7': 'riffle', // 2026-09-27 Grok 4.7 (water-feature noun, like its siblings; no ordering signal)
  'pulse-4.6': 'rapids',
  'pulse-4.5': 'eddy',
  'pulse-4.20': 'shoal',
  'pulse-4.3': 'brook',
  // z-ai / GLM
  'zenith-5.3': 'tor', // 2026-08-24: tier-4 F3 entry (quiet terrain noun, no ordering signal; the -flash build shares it)
  // Qwen (Quill)
  'quill-3.8': 'delta', // 2026-09-01 re-ladder: the Qwen3.8 generation (max/flash/27b share it)
  'zenith-5.2': 'ridge',
  // DeepSeek
  'fathom-4': 'trench',
  // MiniMax
  'maxim-3': 'crag',
  // Moonshot / Kimi
  'lunar-3': 'tide',
  'lunar-2.7': 'cove',
  'lunar-2.6': 'reef',
  // Xiaomi / MiMo
  'mirage-2.5': 'dusk',
  // Qwen
  'quill-3.7': 'sedge',
  // Meta Muse Spark
  'motif-1.3': 'weft', // 2026-09-04: Muse Spark 1.3 enters tier-19
  'motif-1.2': 'loom',
  // Perplexity products (per-product minting — see header note). The BASE
  // search product stays bare "echo" on purpose: its internal code ('echo')
  // is also the provider-only fallback for usage rows with no model id, so a
  // mint word here would claim base-search identity for unknown-product rows.
  'echo-pro': 'moss',
  'echo-reasoning-pro': 'heath',
  'echo-deep': 'vale', // deep research
  // 2026-08-21: base search earns its own word (same moor/heath/moss/vale
  // family). Bare 'echo' — the provider-only fallback — deliberately keeps NO
  // word, so an unknown-product row can never masquerade as base search.
  'echo-search': 'moor',
  // Google product lines (2026-08-21), same landform family as the chat
  // generations above.
  'titan-3.1-image': 'wadi',
  'titan-3-image-pro': 'karst',
  'titan-2.5-image': 'loess',
  'titan-veo': 'scarp', // video product (any veo generation)
  'titan-embed': 'basin', // embeddings product (any generation)
  // ── 2026-09-01 re-ladder products (minted 2026-09-02) ────────────────────
  // OpenAI non-text lines, minted PER PRODUCT on the echo/audio precedent:
  // gpt-image bills nothing like the chat generation it shares a family with,
  // so one shared word would hide the spread in the ⓘ popup.
  'prism-image': 'cinder',
  // Longest-prefix win over 'prism-5.4' (mica): gpt-5.4-image-2 is the IMAGE
  // product, not the 5.4 chat generation, and rendered as 'prism mica' before.
  'prism-5.4-image': 'cinder',
  'prism-embed': 'seam',
  // Google video product line (landform family, as titan-veo / titan-embed).
  'titan-omni': 'fjord',
  // Google VOICE/live product line (landform family, as titan-omni / titan-veo).
  // The Live API build is a different PRODUCT CLASS from the 3.1 chat
  // generation it shares a version with: it billed $3/$12 per million against
  // gemini-3.1-pro's text rate, and both resolved through 'titan-3.1' to the
  // same "titan butte" — the tier-10-voice primary and the tier-14 chat
  // fallback, indistinguishable in the ⓘ popup at very different prices. This
  // is the prism-realtime/prism-image case for Google, and the 2026-09-02
  // non-text registration missed it because voice was the one Google line with
  // no product word. Keyed WITH the version (as the titan-*-image lines are,
  // NOT as titan-veo is): 'titan-3.1-live' is the codename three reverse maps
  // already key pricing off (aiModelPricing, proxyClient, accountExpense), and
  // renaming it to a 'titan-live-*' product shape would strand persisted rows.
  // A future gemini-N-flash-live therefore mints its own word rather than
  // inheriting this one; the versioned-rung gate below fails the build until it
  // does, which is the designed safety net.
  'titan-3.1-live': 'cirque',
  // MiniMax VIDEO line. Distinct from the m3 TEXT generation, which also
  // versions as "3": both resolved to 'maxim-3' before, so a video call and a
  // text call rendered as the same "maxim crag" at very different prices.
  'maxim-video': 'talus',
  'quill-embed': 'gneiss',
  // New families.
  'beacon-2': 'obsidian',
  'cipher-2.5': 'shingle',
  'atlas-w': 'moraine',
  'atlas-h': 'esker',
  'arbor-2': 'granite',
  'vantage-4': 'feldspar',
  // ── 2026-09-08 Tier 22 Sound & Music Design (minted the same day) ─────────
  // ElevenLabs PRODUCT lines beside the bare 'chorus' agents rung: sound
  // effects and music bill per generation, nothing like the agents minute.
  'chorus-sfx': 'inlet',
  'chorus-music': 'lyre',
  // fal-hosted third-party audio lines (harbor family, opaque line letters as
  // the Alibaba video lines): s = a text-to-audio line, m = a foley line.
  'harbor-s': 'quay',
  'harbor-m': 'jetty',
  // the video-input line (mmaudio video-to-audio): its own letter, its own word — same version, different price.
  'harbor-v': 'wharf',
  // Google music product line (landform family, as titan-omni / titan-veo).
  'titan-lyria': 'bluff',
  // ── 2026-09-28 Tier 23 Fast Decisions ────────────────────────────────────
  // TypeSafe's System One decision model. Minted per exact version (as apex-5.5):
  // the vendor's next build needs its own word before it can ship unnamed.
  'reflex-1.13': 'scree',
  // ── 2026-10-12 Tier 8 zero-data-retention re-ladder ──────────────────────
  // Microsoft's MAI-Image line (mosaic family). The flash build is minted on its own (the 5.6
  // precedent): it serves at half the primary's image price, and one shared word would hide that
  // spread in the ⓘ popup.
  'mosaic-2.6-flash': 'kaolin',
  'mosaic-2.6': 'travertine',
  // ByteDance's image product (Seedream), minted PER PRODUCT on the prism-image precedent: it bills
  // per picture, nothing like the Seedance video line that shares the cipher family.
  'cipher-image': 'ochre',
  // ── 2026-10-12 Tier 25 Speech to Text and Tier 26 Speech (zero data retention, PR-9) ──
  // Each speech product line is minted on its own (the chorus-sfx / titan-lyria precedent): speech to
  // text bills per second heard and speech per character, nothing like the families' other lines.
  'chorus-stt': 'arroyo',
  'chorus-tts': 'geode',
  'titan-chirp': 'cairn',
  'titan-3.8-tts': 'tephra',
  'mosaic-transcribe': 'halite',
  'mosaic-voice': 'zircon',
};

/** Longest-prefix mint-word lookup for an internal versioned codename. */
function mintWordForCode(code: string): string | null {
  let best: string | null = null;
  let bestLen = 0;
  for (const key of Object.keys(GENERATION_MINT_WORDS)) {
    if ((code === key || code.startsWith(`${key}-`) || code.startsWith(`${key}.`)) && key.length > bestLen) {
      best = GENERATION_MINT_WORDS[key];
      bestLen = key.length;
    }
  }
  return best;
}

// ── Effort code words (2026-08-15 Miles directive) ──────────────────────────
// The effort designator must be CODED, not literal: the raw ladder words
// (none/minimal/low/medium/high/xhigh/max) are vendor API enum values —
// rendering them verbatim both announces the exact level and leaks a vendor
// parameter vocabulary, violating the no-vendor-designation rule. Each level
// maps to one arbitrary lowercase word below; like the mint lexicon, word
// choice carries NO ordering or intensity signal, so the suffix reads as
// more codename vocabulary. The decoder table lives ONLY in
// NAMING-SYSTEM-INTERNAL.md. Data layers keep storing the raw level —
// coding happens at display time only. An unrecognized level renders NO
// suffix (never the raw string).
export const EFFORT_CODE_WORDS: Record<string, string> = {
  none: 'wick',
  minimal: 'reed',
  low: 'fen',
  medium: 'moth',
  high: 'tarn',
  xhigh: 'briar',
  max: 'helm',
};

/** Display code word for a stored effort level; null when absent/unknown
 *  (callers omit the suffix — the raw level must never render). */
export function getEffortCodeWord(effort?: string | null): string | null {
  if (typeof effort !== 'string') return null;
  const v = effort.trim().toLowerCase();
  return EFFORT_CODE_WORDS[v] || null;
}

/**
 * Display-time effort resolver with SSOT fallback — 2026-08-16 (Miles): the
 * effort code word must ALWAYS display for every model step, even when the
 * stored generation/usage entry has no effort field (historical rows, lanes
 * that bypass the server payload builder). The STORED effort always wins when
 * it decodes to a word; otherwise the served model's SSOT-configured thinking
 * level (SSOT_MODEL_EFFORT, generated from config/model-tiers.json in
 * lockstep with the backend resolver in functions/src/lib/generationUsage.ts)
 * supplies the level — the truthful system answer for "what effort does this
 * model run at on our ladders". App tool steps ('app:' ids) have no effort
 * concept and always render bare; off-ladder/unknown models render bare too
 * (never invent a level). Returns the CODE word only — the raw ladder enum
 * must never render (same rule as getEffortCodeWord).
 */
export function resolveEffortCodeWordForDisplay(
  modelId: string | null | undefined,
  effort?: string | null,
): string | null {
  const id = typeof modelId === 'string' ? modelId.trim().toLowerCase() : '';
  if (id.startsWith('app:')) return null; // app tool steps — no effort concept
  const stored = getEffortCodeWord(effort);
  if (stored) return stored;
  if (!id) return null;
  const normalized = id.replace(/^openrouter\//, '').replace(/^~/, '');
  const ssotEffort =
    SSOT_MODEL_EFFORT[normalized] ??
    (normalized.includes('/')
      ? SSOT_MODEL_EFFORT[normalized.slice(normalized.indexOf('/') + 1)]
      : undefined);
  const fromSsot = getEffortCodeWord(ssotEffort);
  if (fromSsot) return fromSsot;
  // 2026-08-21 (Miles): a model with NO effort dial at all — search, image,
  // video, voice, embeddings — still gets a code word rather than rendering
  // bare, and that word is 'helm' (max): these models expose no level to
  // choose, so every call already runs at the full capability the provider
  // offers. Deliberately an EXPLICIT list, not a catch-all: an unrecognized
  // CHAT model must keep rendering bare rather than claim an effort it never
  // ran at.
  if (isNoEffortDialModel(normalized)) return EFFORT_CODE_WORDS.max;
  return null;
}

/** Product families with no reasoning-effort parameter on any provider. */
export function isNoEffortDialModel(modelId: string | null | undefined): boolean {
  const id = typeof modelId === 'string'
    ? modelId.trim().toLowerCase().replace(/^openrouter\//, '').replace(/^~/, '')
    : '';
  if (!id) return false;
  const bare = id.includes('/') ? id.slice(id.indexOf('/') + 1) : id;
  return (
    // Perplexity search products. Plain sonar and sonar-pro expose no effort
    // parameter; sonar-reasoning-pro and sonar-deep-research DO carry one
    // (reasoning_effort low/medium/high — the Feb 6 2026 silent-downgrade
    // landmine is about exactly that dial) and since D14 slice 3b their SSOT
    // rungs pin it, so the SSOT branch above resolves them to their true
    // level before this list is ever consulted. The prefix match stays broad
    // as the last-resort word for sonar builds with no SSOT entry.
    bare.startsWith('sonar') ||
    // Embeddings
    bare.includes('embedding') ||
    bare.includes('embed-') ||
    // Image generation / editing
    bare.includes('image') ||
    // Video
    bare.startsWith('veo') ||
    bare.includes('sora') ||
    bare.includes('gen-4') ||
    bare.includes('dream-machine') ||
    // Voice / realtime / speech
    bare.includes('-live') ||
    bare.includes('whisper') ||
    bare.includes('tts') ||
    bare.includes('audio') ||
    // Image / video / voice / embeddings / captcha vendors registered
    // 2026-09-02 (the 2026-09-01 re-ladder rungs). None exposes a
    // reasoning-effort parameter, so every call already runs at the full
    // capability the provider offers — they render 'helm', not bare.
    bare.startsWith('flux') ||
    bare.includes('seedance') ||
    bare.includes('seedream') || // ByteDance's image product (Tier 8, 2026-10-12)
    bare.startsWith('wan') ||
    bare.startsWith('happy-horse') ||
    bare.includes('text-to-video') ||
    bare.includes('omni-') ||
    bare.includes('sonic') ||
    bare === 'agents' ||
    bare.startsWith('voyage-') ||
    bare.startsWith('recaptcha') ||
    bare === 'turnstile' ||
    // MiniMax's H-line is the VIDEO line (the M-line is text) — no dial.
    bare.startsWith('minimax-h') ||
    // 2026-09-08 Tier 22 sound / music rungs: no reasoning dial on any of them.
    bare.includes('sound') ||
    bare.startsWith('music') ||
    bare.startsWith('lyria') ||
    // OpenAI realtime/voice builds, alongside the '-live' and 'audio' words
    // above (gpt-realtime-1.5, gpt-realtime-<date>).
    bare.includes('realtime') ||
    // 2026-09-28 Tier 23: the System One decision model answers typed
    // questions through a Decisions API that has no effort parameter.
    bare.startsWith('jev-') ||
    // 2026-10-12 Tiers 25 and 26: speech-to-text and text-to-speech models take no reasoning
    // dial ('tts' above already covers the Gemini speech build; 'scribe' covers MAI-Transcribe too).
    bare.includes('scribe') ||
    bare.startsWith('chirp') ||
    bare.startsWith('eleven-v') ||
    bare.startsWith('mai-voice')
  );
}

/**
 * THE canonical public system reference (2026-08-14 Miles directive; effort
 * CODING 2026-08-15): the public name plus the coded effort designator —
 * "apex cedar · tarn". Every surface that shows a codename beside usage
 * (ⓘ popup, activity rows, expense line items, CSV strings) must compose
 * through this so the notation cannot drift. Rows with no stamped effort
 * (or an unrecognized level) render the bare name.
 */
export function formatPublicSystemReference(
  model?: string,
  effort?: string | null,
  provider?: string,
): string {
  const name = getPublicSystemNameForUsage(model, provider);
  const code = getEffortCodeWord(effort);
  return code ? `${name} · ${code}` : name;
}

/**
 * Dot/badge color for a usage row (2026-08-15). The row's `provider` field
 * is often the TRANSPORT ('openrouter') or absent entirely on per-call
 * breakdown rows — coloring by it turned every dot neutral slate, which
 * read as "the color scheme changed". The model id's family is the true
 * vendor: prefer it, fall back to the provider field only when the model is
 * unknown. Same derivation the Activity and Expenses tables already use.
 */
export const getSystemDotColor = (model?: string, provider?: string): string => {
  const detected = detectProviderFromModelId(model);
  if (detected !== 'unknown') return getProviderColor(detected);
  return getProviderColor(provider);
};

/** Compose the full public name: "family mint" when the generation is known,
 *  bare family word otherwise (historical/providerless rows). */
function publicNameFromCode(code: string): string | null {
  const family = code.split('-')[0].toLowerCase();
  const familyWord = SYSTEM_STATION_NAMES[family];
  if (!familyWord) return null;
  const mint = mintWordForCode(code);
  return mint ? `${familyWord} ${mint}` : familyWord;
}

// Public names self-resolve (round-trip safety: a display name fed back in
// returns itself). The retired print-floor station words — public for less
// than a day (2026-08-13) — decode to the family display name so any row or
// cached string minted during that window still renders the current
// vocabulary. These keys only ever match MODEL-IDENTIFIER inputs (exact,
// lowercased), never free prose, so common-word collisions ("tome", "kern")
// are not a concern here.
const LEGACY_STATION_ALIASES: Record<string, string> = {
  handpress: 'apogee',
  halftone: 'apex',
  hinge: 'nova',
  hasp: 'swift',
  vellum: 'prism',
  nonpareil: 'titan',
  escapement: 'pulse',
  galley: 'zenith',
  kern: 'fathom',
  tissue: 'maxim',
  tome: 'lunar',
  etching: 'mirage',
  xylograph: 'quill',
  tessera: 'motif',
  watermark: 'echo',
};
const STATION_SELF_KEYS: Record<string, string> = {
  ...LEGACY_STATION_ALIASES,
  ...Object.fromEntries(
    Object.values(SYSTEM_STATION_NAMES).map((station) => [station.toLowerCase(), station])
  ),
  // Full "family mint" pairs round-trip too: a rendered display name fed back
  // through the resolver returns itself instead of degrading to bare family.
  ...Object.fromEntries(
    Object.entries(GENERATION_MINT_WORDS).map(([code, mint]) => [
      `${code.split('-')[0]} ${mint}`,
      `${code.split('-')[0]} ${mint}`,
    ])
  ),
};

/**
 * Resolve ANY model identifier (raw vendor id, versioned internal codename,
 * or persisted legacy row) to its user-facing station name. This is the ONLY
 * model-identity string that may render in UI. Returns 'System' when the
 * family is unknown and 'Prest0n' for router-attributed turns with no model.
 */
/** Humanize a connected-app slug for display: 'googlecalendar' →
 *  'google calendar' (2026-08-16 Miles: readable app names, real names by
 *  design — the user's own connected apps, not vendor secrets). */
export function humanizeAppSlug(slug: string): string {
  const s = String(slug || '').trim().toLowerCase();
  if (!s) return 'app';
  const KNOWN: Record<string, string> = {
    googlecalendar: 'google calendar',
    googledrive: 'google drive',
    googledocs: 'google docs',
    googlesheets: 'google sheets',
    googlemeet: 'google meet',
    gmail: 'gmail',
    outlook: 'outlook',
    onedrive: 'onedrive',
    slack: 'slack',
    notion: 'notion',
    hubspot: 'hubspot',
    salesforce: 'salesforce',
  };
  if (KNOWN[s]) return KNOWN[s];
  // Generic split for compound google/microsoft slugs; else underscores/dashes → spaces.
  const prefixed = s.match(/^(google|microsoft)(.+)$/);
  if (prefixed) return `${prefixed[1]} ${prefixed[2]}`;
  return s.replace(/[-_]+/g, ' ');
}

/** App-tool system ids: 'app:googlecalendar' → 'google calendar'. */
function appSystemName(model: string): string | null {
  const m = model.trim().toLowerCase();
  if (!m.startsWith('app:')) return null;
  const app = m.slice(4).split(':')[0].trim();
  return humanizeAppSlug(app || 'app');
}

export function getPublicSystemName(model?: string): string {
  if (!model) return 'system';
  const app = appSystemName(String(model));
  if (app) return app;
  // Display names round-trip: check the raw input BEFORE the codename
  // resolver, which does not know the display vocabulary and would degrade it.
  const raw = String(model).trim().toLowerCase();
  if (STATION_SELF_KEYS[raw]) return STATION_SELF_KEYS[raw];
  const code = getModelCodeName(model);
  if (code === 'prest0n-routed') return 'Prest0n';
  const composed = publicNameFromCode(code);
  if (composed) return composed;
  return 'system';
}

/**
 * Usage-row variant: falls back to the provider family when the record has
 * no model id (mirrors getModelCodeNameForUsage) so usage popups always show
 * a station instead of 'System' when the provider is known.
 */
export function getPublicSystemNameForUsage(model?: string, provider?: string): string {
  if (model) {
    const app = appSystemName(String(model));
    if (app) return app;
    const raw = String(model).trim().toLowerCase();
    if (STATION_SELF_KEYS[raw]) return STATION_SELF_KEYS[raw];
  }
  const code = getModelCodeNameForUsage(model, provider);
  if (code === 'prest0n-routed') return 'Prest0n';
  const composed = publicNameFromCode(code);
  if (composed) return composed;
  const family = code.split('-')[0].toLowerCase();
  if (STATION_SELF_KEYS[family]) return STATION_SELF_KEYS[family];
  return 'system';
}

// Provider code names (used in error messages and UI). 2026-08-13 (family
// vocabulary restored, Miles directive): values are the vendor's
// workhorse-line FAMILY display name, versionless — same vocabulary the ⓘ
// popup renders, so every user-facing surface speaks one language.
export const PROVIDER_CODE_NAMES: Record<string, string> = {
  perplexity: 'echo',
  anthropic: 'apex',
  claude: 'apex',
  openai: 'prism',
  gemini: 'titan',
  google: 'titan',
  grok: 'pulse',
  'x-ai': 'pulse',
  xai: 'pulse',
  'z-ai': 'zenith',
  zai: 'zenith',
  deepseek: 'fathom',
  minimax: 'maxim',
  moonshot: 'lunar',
  moonshotai: 'lunar',
  xiaomi: 'mirage',
  qwen: 'quill',
  meta: 'motif', // Meta Muse Spark line (family minted 2026-08-07)
  // Non-text product vendors (2026-09-02)
  'black-forest-labs': 'beacon',
  bytedance: 'cipher',
  alibaba: 'atlas',
  elevenlabs: 'chorus',
  amazon: 'arbor',
  voyageai: 'vantage',
  '2captcha': 'sentry',
  // fal-hosted audio lines (2026-09-08, Tier 22)
  'fal-ai': 'harbor',
  // System One decisions (2026-09-28, Tier 23)
  typesafe: 'reflex',
  // Tier 8 image vendors reached through the model gateway (2026-10-12)
  microsoft: 'mosaic',
  'bytedance-seed': 'cipher',
  unknown: 'system',
};

// Reverse mapping for internal use (code name -> real provider).
// Keeps BOTH vocabularies: internal versioned families (apex/nova/prism/…,
// required for persisted-row round-trips and detectProviderFromModelId) AND
// the 2026-08-13 station names (so a station word fed back through any
// resolver still decodes to its provider).
export const PROVIDER_FROM_CODE_NAME: Record<string, string> = {
  // Retired print-floor station words (public <1 day, 2026-08-13) — kept so
  // any row minted in that window still decodes to its provider.
  watermark: 'perplexity',
  halftone: 'claude',
  handpress: 'claude',
  hinge: 'claude',
  hasp: 'claude',
  vellum: 'openai',
  nonpareil: 'gemini',
  escapement: 'grok',
  galley: 'zai',
  kern: 'deepseek',
  tissue: 'minimax',
  tome: 'moonshot',
  etching: 'xiaomi',
  xylograph: 'qwen',
  tessera: 'meta',
  // Family names — BOTH the internal versioned-codename families AND (since
  // the 2026-08-13 restore) the public display vocabulary, so a display name
  // fed back through any resolver decodes to its provider.
  echo: 'perplexity',
  apex: 'claude',
  nova: 'claude',
  apogee: 'claude', // Claude Fable frontier family (minted 2026-07-10)
  swift: 'claude', // legacy Haiku family — persisted 'swift-*' rows round-trip through the resolver
  prism: 'openai',
  titan: 'gemini',
  spark: 'gemini', // legacy code name (retired 2026-07-06; kept for old records)
  pulse: 'grok',
  zenith: 'zai',
  fathom: 'deepseek',
  maxim: 'minimax',
  lunar: 'moonshot',
  mirage: 'xiaomi',
  quill: 'qwen',
  motif: 'meta', // Meta Muse Spark line (minted 2026-08-07)
  // Non-text product vendors (2026-09-02)
  beacon: 'black-forest-labs',
  cipher: 'bytedance',
  atlas: 'alibaba',
  chorus: 'elevenlabs',
  arbor: 'amazon',
  vantage: 'voyageai',
  sentry: '2captcha',
  harbor: 'fal-ai',
  reflex: 'typesafe',
  mosaic: 'microsoft',
  system: 'unknown',
};

// Provider color scheme (unique color for each AI provider family). Lives in
// this leaf config module (no React/component imports) so both the chat popup
// and tool tiles share ONE map without import cycles — the SpecialMessageTile
// local copy had drifted to a 4-provider subset (2026-07-06 consolidation).
// Note: dark colors (like Perplexity black) use white text in SystemBadge.
// 2026-08-13 rotation: badge colors re-cut alongside the station rename.
// The old palette borrowed vendor-brand hues (Anthropic orange, Perplexity
// black, Meta blue) — a correlation handle in its own right. The new "ink"
// palette is deliberately brand-neutral; no color matches its vendor's
// public identity, and the rename + recolor land together so old dot colors
// can't bridge the community's existing decoder tables to the new names.
// Palette restored 2026-08-13 (Miles, round 8): the print-floor repalette put
// Halftone (#0E7490 teal) and Escapement (#15803D green) nearly on top of each
// other in chart bars — "the colors … are the same". These are the ORIGINAL
// high-contrast family hues from before the station migration (the scheme the
// owner liked), with two collisions re-spaced so every family stays tellable
// apart at chip size: deepseek moved off openai's purple, gemini moved off
// meta's royal blue. Station NAMES are unchanged — only the hex values revert.
// 2026-08-13 (family vocabulary restored): hex values untouched — this is
// the round-8 palette the owner restored earlier today; only the display
// vocabulary in the comments moved back to the family names.
export const PROVIDER_COLORS: Record<string, string> = {
  perplexity: '#1a1a1a',  // Echo — ink black
  claude: '#E07D50',      // Apex — anthropic orange
  openai: '#8B5CF6',      // Prism — purple
  gemini: '#0EA5E9',      // Titan — sky (off meta's royal blue)
  google: '#0EA5E9',      // alias of gemini
  grok: '#DC2626',        // Pulse — red
  'x-ai': '#DC2626',      // alias of grok
  zai: '#10B981',         // Zenith — emerald
  'z-ai': '#10B981',      // alias of zai
  deepseek: '#4338CA',    // Fathom — deep indigo (off openai's purple)
  minimax: '#F59E0B',     // Maxim — amber
  moonshot: '#0D9488',    // Lunar — teal
  moonshotai: '#0D9488',  // alias of moonshot
  xiaomi: '#D946EF',      // Mirage — fuchsia
  qwen: '#84CC16',        // Quill — lime
  meta: '#0668E1',        // Motif — meta royal blue
  composio: '#C6A75E',   // app tool calls — brand gold (the user's connected apps)
  openrouter: '#475569',  // routed-without-model-id (prest0n-routed)
  // Non-text product vendors (2026-09-02). The bright end of the palette is
  // fully spoken for by the chat families above, so these deliberately take
  // darker/desaturated hues — they stay tellable apart at chip size without
  // re-cutting the established chat colors.
  'black-forest-labs': '#78350F', // Beacon — bark brown
  bytedance: '#EC4899',           // Cipher — pink
  alibaba: '#A16207',             // Atlas — ochre
  elevenlabs: '#7E22CE',          // Chorus — violet
  amazon: '#155E75',              // Arbor — deep cyan
  voyageai: '#3F6212',            // Vantage — olive
  '2captcha': '#57534E',          // Sentry — stone
  'fal-ai': '#0F766E',            // Harbor — deep teal (2026-09-08)
  typesafe: '#9D174D',            // Reflex — raspberry (2026-09-28, Tier 23)
  microsoft: '#1E3A8A',           // Mosaic — navy (2026-10-12, Tier 8)
  'bytedance-seed': '#EC4899',    // Cipher — pink, the ByteDance family (Seedream image)
  unknown: '#64748b',     // Slate
};

// Get provider color (case-insensitive; unknown providers get the slate dot)
export const getProviderColor = (provider?: string): string => {
  const key = (provider || 'unknown').toLowerCase();
  return PROVIDER_COLORS[key] || PROVIDER_COLORS.unknown;
};

/**
 * Get the code name for a provider.
 * Returns 'System' for unknown providers.
 */
export function getProviderCodeName(provider?: string): string {
  if (!provider) return 'System';
  return PROVIDER_CODE_NAMES[provider.toLowerCase()] || 'System';
}

/**
 * Get the real provider name from a code name.
 * Returns 'unknown' for unrecognized code names.
 */
export function getProviderFromCodeName(codeName?: string): string {
  if (!codeName) return 'unknown';
  return PROVIDER_FROM_CODE_NAME[codeName.toLowerCase()] || 'unknown';
}

/**
 * Error types for standardized error messages.
 */
export type ProviderErrorType =
  | 'api_key_missing'
  | 'request_failed'
  | 'empty_response'
  | 'network_error'
  | 'fallback_failed';

/**
 * Format a standardized provider error message using code names.
 * Use this for all user-facing error messages.
 *
 * @param provider - The real provider name (e.g., 'perplexity', 'anthropic')
 * @param errorType - The type of error
 * @param statusCode - Optional HTTP status code for request failures
 * @returns User-facing error message with code name
 */
export function formatProviderError(
  provider: string,
  errorType: ProviderErrorType,
  statusCode?: number
): string {
  const codeName = getProviderCodeName(provider);

  switch (errorType) {
    case 'api_key_missing':
      return `${codeName} API key is missing.`;
    case 'request_failed':
      return statusCode
        ? `${codeName} request failed (${statusCode})`
        : `${codeName} request failed`;
    case 'empty_response':
      return `${codeName} returned an empty response.`;
    case 'network_error':
      return `Network error calling ${codeName}`;
    case 'fallback_failed':
      return `${codeName} request failed and no fallback models succeeded.`;
    default:
      return `${codeName} error`;
  }
}

/**
 * Sanitize an error message by replacing any provider names with code names.
 * Use this when error messages from external APIs may contain provider names.
 *
 * @param message - The raw error message
 * @returns Sanitized message with provider names replaced
 */
export function sanitizeProviderNames(message: string): string {
  if (!message) return message;

  return message
    .replace(/\bperplexity\b/gi, 'echo')
    .replace(/\banthropic\b/gi, 'apex')
    .replace(/\bclaude\b/gi, 'apex')
    .replace(/\bopenai\b/gi, 'prism')
    .replace(/\bgemini\b/gi, 'titan')
    .replace(/\bgoogle\b/gi, 'titan')
    .replace(/\bgrok\b/gi, 'pulse')
    .replace(/\bx-ai\b/gi, 'pulse')
    .replace(/\bz-ai\b/gi, 'zenith')
    .replace(/\bglm\b/gi, 'zenith')
    .replace(/\bdeepseek\b/gi, 'fathom')
    .replace(/\bminimax\b/gi, 'maxim')
    .replace(/\bmoonshot(ai)?\b/gi, 'lunar')
    .replace(/\bkimi\b/gi, 'lunar')
    .replace(/\bxiaomi\b/gi, 'mirage')
    .replace(/\bmimo\b/gi, 'mirage')
    .replace(/\bqwen\b/gi, 'quill')
    // VERSIONED family forms (apex-4.8, prism-5.6-s…) must never leak their
    // digits — collapse to the bare versionless display name (all-lowercase
    // per the 2026-08-14 casing directive). Bare family words need no
    // rewrite: they ARE the public vocabulary now.
    .replace(/\bapogee(-[\w.]+)+\b/gi, 'apogee')
    .replace(/\bapex(-[\w.]+)+\b/gi, 'apex')
    .replace(/\bnova(-[\w.]+)+\b/gi, 'nova')
    .replace(/\bprism(-[\w.]+)+\b/gi, 'prism')
    .replace(/\btitan(-[\w.]+)+\b/gi, 'titan')
    .replace(/\bpulse(-[\w.]+)+\b/gi, 'pulse')
    .replace(/\bzenith(-[\w.]+)+\b/gi, 'zenith')
    .replace(/\bfathom(-[\w.]+)+\b/gi, 'fathom')
    .replace(/\bmaxim(-[\w.]+)+\b/gi, 'maxim')
    .replace(/\blunar(-[\w.]+)+\b/gi, 'lunar')
    .replace(/\bmirage(-[\w.]+)+\b/gi, 'mirage')
    .replace(/\bquill(-[\w.]+)+\b/gi, 'quill')
    .replace(/\bmotif(-[\w.]+)+\b/gi, 'motif')
    .replace(/\becho(-[\w.]+)+\b/gi, 'echo')
    .replace(/\bswift(-[\w.]+)+\b/gi, 'swift')
    .replace(/\bspark(-[\w.]+)+\b/gi, 'titan')
    // Retired print-floor station words (public <1 day, 2026-08-13): rewrite
    // only the DISTINCTIVE ones — hinge/hasp/galley/kern/tissue/tome/etching/
    // watermark are common English words that legitimately appear in error
    // prose (e.g. PDF watermarking), so they are left alone; the resolver
    // aliases still decode them when they arrive as model identifiers.
    .replace(/\bhandpress\b/gi, 'apogee')
    .replace(/\bhalftone\b/gi, 'apex')
    .replace(/\bvellum\b/gi, 'prism')
    .replace(/\bnonpareil\b/gi, 'titan')
    .replace(/\bescapement\b/gi, 'pulse')
    .replace(/\bxylograph\b/gi, 'quill')
    .replace(/\btessera\b/gi, 'motif');
}

// ============================================================================
// HTTP Status Code User-Friendly Messages
// ============================================================================

/**
 * User-friendly messages for common HTTP status codes.
 * These provide actionable guidance instead of technical error codes.
 */
// copy: http_error_messages
export const HTTP_ERROR_MESSAGES: Record<number, string> = {
  400: 'Invalid request - please check your input',
  401: 'Authentication required - please sign in',
  403: 'Access denied - you don\'t have permission',
  404: 'Not found - the requested resource doesn\'t exist',
  408: 'Request timed out - please try again',
  429: 'Too many requests - please wait a moment and try again',
  500: 'Server error - our systems are experiencing issues',
  502: 'Service temporarily unavailable - please try again',
  503: 'Service busy - please try again in a moment',
  504: 'Gateway timeout - please try again',
};

/**
 * Get a user-friendly error message for an HTTP status code.
 *
 * @param status - The HTTP status code
 * @returns User-friendly error message
 */
export function getHttpErrorMessage(status: number): string {
  return HTTP_ERROR_MESSAGES[status] || `Request failed (error ${status})`;
}

/**
 * Format an HTTP error with both status code and user-friendly message.
 *
 * @param status - The HTTP status code
 * @returns Formatted error string with code and message
 */
export function formatHttpError(status: number): string {
  const message = HTTP_ERROR_MESSAGES[status];
  return message || `Request failed with status ${status}`;
}

// ============================================================================
// Model code names (UI-facing). Moved here from
// components/GlobalChatSystem/GlobalChatSystem.tsx so that non-UI modules
// (e.g. services/agenticWorkflows/helpers.ts) can import getModelCodeName
// without pulling in the GlobalChatSystem component module. That import edge
// formed a circular dependency: helpers.ts -> GlobalChatSystem.tsx ->
// useGlobalChat.ts (which calls registerAllTools() at module load), causing a
// tool binding to be undefined mid-cycle and silently aborting registration.
// Keeping these in this leaf config module (no React/component imports) breaks
// the cycle. GlobalChatSystem re-exports them for backward compatibility.
// ============================================================================

export const MODEL_CODE_NAMES: Record<string, string> = {
  // Claude models
  // Apogee (Claude Fable — frontier family, minted 2026-07-10 for the tier-17/18
  // rungs; 'fable' is a vendor model designation and must not leak, 2026-07-07 rule).
  'claude-fable-5.1': 'apogee-5.1', // 2026-09-01 rotation: the live Fable generation (Fable 5 retired; its row stays for historical rows)
  'claude-fable-5': 'apogee-5',
  'claude-fable-latest': 'apogee-5.1', // historical alias (retired 2026-09-29), frozen at 5.1
  '~anthropic/claude-fable-latest': 'apogee-5.1',
  // Opus. The alias forms are historical (floating aliases retired 2026-09-29) and
  // frozen at the generation they last served; exact rows keep pricing lookups and
  // persisted-row round-trips off the unversioned regex fallback (apex). INTERNAL ONLY — since the 2026-08-13
  // migration every user-visible surface renders through
  // getPublicSystemName()/getPublicSystemNameForUsage() (digit-free
  // family+mint, e.g. "apex cedar"); versioned codes never reach a user.
  '~anthropic/claude-opus-latest': 'apex-5.5', // raw always-latest alias id — historical (retired 2026-09-29), frozen at Opus 5.5
  'claude-opus-latest': 'apex-5.5',
  'claude-opus-5': 'apex-5',
  'claude-opus-5.5': 'apex-5.5', // 2026-09-27 tier-1 lead (Opus 5.5)
  'claude-opus-4.8': 'apex-4.8',
  'claude-opus-4-8': 'apex-4.8', // dash-variant alias
  'claude-4.8-opus-20260528': 'apex-4.8', // OpenRouter resolved id for ~anthropic/claude-opus-latest
  'claude-opus-4.7': 'apex-4.7',
  'claude-opus-4-7': 'apex-4.7', // dash-variant alias
  'claude-opus-4-6': 'apex-4.6', // legacy decode
  'claude-opus-4-5-20251101': 'apex-4.5', // Legacy backward compat
  // Sonnet 5 — sanctioned 2026-07-02 (tier-2 first fallback per 2026-07-05
  // SSOT). Nova family revived for Sonnet majors >= 5.
  '~anthropic/claude-sonnet-latest': 'nova-5.5', // historical alias (retired 2026-09-29), frozen at Sonnet 5.5
  'claude-sonnet-latest': 'nova-5.5',
  'claude-sonnet-5.5': 'nova-5.5', // 2026-09-28; tier-5 Fallback 2 since 2026-10-02
  'claude-sonnet-5': 'nova-5',
  // 2026-05-10 (Miles directive): Sonnet/Haiku majors <= 4 retired from active
  // Claude paths. Legacy persisted refs remap to apex-4.7 so historical
  // messages still resolve to a valid Claude codename.
  'claude-sonnet-4-6': 'apex-4.7', // legacy remap to Apex-4.7 (was nova-4.6)
  'claude-sonnet-4-20250514': 'apex-4.7', // legacy remap to Apex-4.7 (was nova-4)
  'claude-haiku-4-5-20251001': 'apex-4.7', // legacy remap to Apex-4.7 (was swift-4.5)
  'claude-haiku-4.5': 'apex-4.7', // legacy remap to Apex-4.7 (2026-07-06: aligned with the dated variant above)
  // Perplexity models
  'sonar-reasoning-pro': 'echo-reasoning-pro',
  'sonar-deep-research': 'echo-deep',
  'sonar-pro': 'echo-pro',
  // 2026-08-21 (Miles: "echo doesn't have a secondary name as it should"):
  // base search gets its OWN internal code so it can carry a mint word.
  // Bare 'echo' is RETAINED below as the provider-only/unknown-model fallback
  // — that is the row shape that must never claim base-search identity.
  'sonar': 'echo-search',
  // OpenAI models
  'gpt-6-astra': 'prism-6-a', // 2026-09-04 (Miles): every Sol rung -> Astra; T1/T2/T19 PRIMARY + fallback on tiers 4/5/7/11/16/17/18/20/21
  'gpt-6.1-sol': 'prism-6.1-s', // 2026-09-30: tier-4 Fallback 1 (low); 2026-10-02: tier-1 Fallback 3 (high), its home tier
  'gpt-6-sol': 'prism-6-s', // 2026-09-30: on no ladder yet
  'gpt-6-luna': 'prism-6-l', // 2026-09-30: on no ladder yet
  'muse-spark-1.3': 'motif-1.3', // 2026-09-04: tier-19 tail
  'gpt-5.6-sol': 'prism-5.6-s', // tier-5 PRIMARY (SWE plane, 2026-07-31) + fallback on tiers 1/2/11/16/17/18. 2026-07-31 codename obfuscation (Miles): single-letter variant suffixes (S/L/T = sol/luna/terra, initial-letter congruent) — real variant names must never be user-visible
  'gpt-5.6-luna': 'prism-5.6-l', // tier-3 PRIMARY (2026-07-31) + tier-15 fallback rung
  'gpt-5.6-terra': 'prism-5.6-t', // ENTERS 2026-07-31: fleet-default primary (LATE rotation) + tier-6 primary + tier-19 tail
  'gpt-5.3-codex': 'prism-5.3', // tier-5 rung 3 (2026-07-31) — left tier-2 in the 2026-07-10 grok-4.5 rebuild (2026-07-07 Miles directive: no vendor model designations like 'codex' in codenames)
  'gpt-5.5': 'prism-5.5',
  'gpt-latest': 'prism-5.6-s', // historical alias (retired 2026-09-29), frozen at gpt-5.6-sol
  'gpt-mini-latest': 'prism-5.4-mini', // historical alias (retired 2026-09-29), frozen at 5.4-mini
  'gpt-5.4': 'prism-5.4',
  'gpt-5.4-mini': 'prism-5.4-mini',
  'gpt-5.4-nano': 'prism-5.4-nano',
  'gpt-4-turbo': 'prism-4t',
  // tier-10-voice fallback rungs (2026-08-19)
  'gpt-realtime-1.5': 'prism-realtime-1.5', // 2026-09-01 tier-10 re-ladder
  'gpt-realtime-2025-08-28': 'prism-realtime',
  'gpt-audio': 'prism-audio',
  'gpt-audio-mini': 'prism-audio-mini',
  'gpt-4': 'prism-4',
  'gpt-3.5-turbo': 'prism-3',
  // Google models
  'gemini-pro-latest': 'titan-pro', // historical alias (retired 2026-09-29)
  'gemini-flash-latest': 'titan-3.8-flash', // historical alias (retired 2026-09-29), frozen at 3.8
  'gemini-3.8-flash': 'titan-3.8-flash',
  'gemini-3.7-flash': 'titan-3.7-flash',
  'gemini-3.6-flash': 'titan-3.6-flash',
  'gemini-3.5-flash': 'titan-3.5-flash', // tier-2 fallback rung
  'gemini-3.1-pro-preview': 'titan-3.1',
  // tier-10-voice primary (2026-08-19). Same generation as titan-3.1, so it
  // inherits that generation's mint word — Live is a serving mode of Gemini
  // 3.1, not a new generation.
  'gemini-3.1-flash-live-preview': 'titan-3.1-live',
  // 2026-08-21 (Miles: "titan doesn't have a secondary name nor effort level
  // codes as it should"). These product models were absent from this table
  // entirely, so every image/video/embedding row rendered as bare "titan" with
  // no generation word and no effort code. Named on the landform convention.
  'gemini-3.1-flash-image-preview': 'titan-3.1-image',
  'gemini-3.1-flash-image': 'titan-3.1-image',
  'gemini-3-pro-image-preview': 'titan-3-image-pro',
  'gemini-2.5-flash-image': 'titan-2.5-image',
  'veo-3.1': 'titan-veo-3.1',
  'gemini-embedding-2-preview': 'titan-embed-2',
  'gemini-embedding-2': 'titan-embed-2',
  // x-ai Grok
  'grok-4.7': 'pulse-4.7', // 2026-09-27 tier-1 fallback (Grok 4.7)
  'grok-4.6': 'pulse-4.6', // tier-4 General Default PRIMARY + tier-2 F1 + tier-14 tail via ~x-ai/grok-latest (2026-08-12 re-ladder + all-grok-4.5 follow-up)
  'grok-latest': 'pulse-4.7', // historical alias (retired 2026-09-29), frozen at grok-4.7
  'grok-4.5': 'pulse-4.5', // historical (left every ladder in the 2026-08-12 all-grok-4.5 follow-up)
  'grok-4.20': 'pulse-4.20', // historical (left tier-2 2026-07-10)
  'grok-4.3': 'pulse-4.3', // historical (left tier-14 2026-07-10)
  // z-ai GLM
  'glm-5.3': 'zenith-5.3', // tier-4 Fallback 3 (2026-08-24 re-ladder — first ladder appearance)
  'glm-5.3-flash': 'zenith-5.3-flash', // 2026-09-01 re-ladder (tier 7; shares the zenith-5.3 generation word)
  'glm-5.2': 'zenith-5.2', // tier-6 tail (2026-07-31 revival; left tier-2 2026-07-10)
  // Tier-3 efficiency ladder
  'minimax-m3': 'maxim-3',
  'deepseek-v4-pro': 'fathom-4-pro',
  'deepseek-v4-pro-0813': 'fathom-4-pro-0813', // tier-4 tail (2026-08-24 re-ladder; dated build — mint word 'trench' shared via the fathom-4 generation prefix)
  'kimi-k3': 'lunar-3', // tier-5 rung 4 (2026-07-31; entered tier-2 2026-07-17) — Moonshot flagship
  'kimi-k2.6': 'lunar-2.6',
  'kimi-k2.7-code': 'lunar-2.7-code', // tier-6 rung 4 (lane REVIVED 2026-07-31); '-code' is a variant descriptor like Sol/Luna, kept to disambiguate any future general k2.7
  'mimo-v2.5-pro': 'mirage-2.5-pro',
  // Qwen (tier-11 fallback rung)
  'qwen3.8-max': 'quill-3.8-max', // 2026-09-01 re-ladder (tiers 3/4); historical since 2026-09-30a
  'qwen3.8-max-0902': 'quill-3.8-max-0902', // 2026-09-30a (tiers 3/4; dated snapshot, mint word shared via the quill-3.8 generation prefix)
  'qwen3.8-flash': 'quill-3.8-flash', // 2026-09-01 re-ladder (tier 6)
  'qwen3.8-27b': 'quill-3.8-27b', // 2026-09-01 re-ladder (tier 7)
  'qwen3.7-max': 'quill-3.7-max',
  // Meta Muse Spark — Motif family minted 2026-08-07 (tier-4 General Default
  // PRIMARY per the 2026-08-07 re-ladder). 'muse'/'spark' are vendor model
  // designations and must never leak (2026-07-07 rule); 'motif' is
  // M-congruent per the maxim/mirage/zenith precedent.
  'muse-spark-1.2': 'motif-1.2',
  // ── 2026-09-01 re-ladder rungs (registered 2026-09-02) ───────────────────
  // These 18 ids sit on live tier-8/9/10/12/15 ladders and had NO registry
  // row, so 12 fell through to 'unknown' (rendered "system") and 6 mis-landed
  // via the regex fallback — including two cost-misleading collisions:
  // MiniMax-H3 (video) → 'maxim-3', the same as MiniMax M3 (text), and
  // gpt-5.4-image-2 → 'prism-5.4', the same as GPT-5.4 chat.
  // OpenAI product lines (tier-8 image, tier-12 embeddings)
  'gpt-image-2': 'prism-image-2',
  'gpt-image-1.5': 'prism-image-1.5',
  'gpt-5.4-image-2': 'prism-5.4-image-2',
  'text-embedding-3-large': 'prism-embed-3-large',
  // Google video (tier-9). 'omni' is kept only as the internal PRODUCT key —
  // the public name is family+mint ('titan fjord'), as with titan-veo.
  'gemini-omni-1.1-flash': 'titan-omni-1.1',
  // MiniMax video (tier-9) — its own product code, never the m3 text row.
  'minimax-h3': 'maxim-video-3',
  // Qwen embeddings (tier-12)
  'qwen3-embedding-8b': 'quill-embed-3',
  // Black Forest Labs (tier-8)
  'black-forest-labs/flux-2-max': 'beacon-2-max',
  // ByteDance (tier-9)
  'bytedance/dreamina-seedance-2-5-260628': 'cipher-2.5',
  // Alibaba video lines (tier-9) — Qwen stays on quill; these are separate lines
  'alibaba/wan2.7-t2v-2026-06-12': 'atlas-w-2.7',
  'alibaba/happy-horse/v1.1/text-to-video': 'atlas-h-1.1',
  // ElevenLabs (tier-10) — single product line, no generation to mint
  'elevenlabs/agents': 'chorus',
  // ── 2026-09-08 Tier 22 Sound & Music Design ─────────────────────────────
  // Keys are the NORMALIZED ids getModelCodeName produces (underscores become
  // dashes; 'google/' is stripped; 'elevenlabs/' and 'fal-ai/' are not).
  'elevenlabs/eleven-text-to-sound-v2': 'chorus-sfx-2',
  'elevenlabs/music-v2': 'chorus-music-2',
  'fal-ai/stable-audio-25/text-to-audio': 'harbor-s-2.5',
  'fal-ai/mmaudio-v2/text-to-audio': 'harbor-m-2',
  'fal-ai/mmaudio-v2': 'harbor-v-2',
  'lyria-3-clip-preview': 'titan-lyria-3',
  // ── 2026-10-12 Tiers 25 and 26 (zero data retention, PR-9) ─────────────
  // Google's speech builds ('google/' is stripped). The ElevenLabs and Microsoft speech lines
  // resolve through nonTextVendorCode, version-aware; their pinned builds are listed too.
  'chirp-3': 'titan-chirp-3',
  'gemini-3.8-flash-tts': 'titan-3.8-tts',
  'elevenlabs/scribe-v2': 'chorus-stt-2',
  'elevenlabs/eleven-v4-turbo': 'chorus-tts-4-turbo',
  'microsoft/mai-transcribe-2': 'mosaic-transcribe-2',
  'microsoft/mai-voice-2.1': 'mosaic-voice-2.1',
  // Amazon (tier-10). NOTE the vendor prefix is deliberately NOT stripped in
  // getModelCodeName: bare 'nova-2-sonic-v1:0' would hit the codename
  // passthrough and claim the Anthropic Sonnet family.
  'amazon/nova-2-sonic-v1:0': 'arbor-2',
  // Voyage AI (tier-12)
  'voyageai/voyage-4-large': 'vantage-4-large',
  'voyageai/voyage-4': 'vantage-4',
  // 2captcha solver endpoints (tier-15) — challenge types, not generations
  '2captcha/recaptchav2enterprise': 'sentry',
  '2captcha/turnstile': 'sentry',
  '2captcha/image-ocr': 'sentry',
  // ── 2026-09-28 Tier 23 Fast Decisions ───────────────────────────────────
  // 'typesafe/' is not a stripped transport prefix, so the key keeps it.
  'typesafe/jev-1.13': 'reflex-1.13',
  // ── 2026-10-12 Tier 8 zero-data-retention re-ladder ──────────────────────
  // 'microsoft/' and 'bytedance-seed/' are not stripped transport prefixes, so the keys keep them
  // ('google/' is stripped, so the Gemini image key does not).
  'microsoft/mai-image-2.6': 'mosaic-2.6',
  'microsoft/mai-image-2.6-flash': 'mosaic-2.6-flash',
  'bytedance-seed/seedream-5-0-pro': 'cipher-image-5.0-pro',
  'gemini-3-pro-image': 'titan-3-image-pro',
};

// Get internal code name for a model
// Returns mapped code name if available; otherwise resolves through the
// regex families below. NEVER returns a raw vendor model id (white-label
// mandate) — unresolvable ids return 'unknown' so callers can fall back to
// the provider family via getModelCodeNameForUsage.
/**
 * Family resolver for the non-text product vendors registered 2026-09-02
 * (image / video / voice / embeddings / captcha). Consulted AFTER the exact
 * MODEL_CODE_NAMES lookup, so a pinned rung always wins; this only catches
 * NEW builds from a known vendor, which would otherwise fall to 'unknown'.
 *
 * Matched on the RAW vendor prefix on purpose. These vendors are deliberately
 * absent from the provider-strip list in getModelCodeName: stripping
 * 'amazon/' would leave 'nova-2-sonic-v1:0', which the codename passthrough
 * would then hand to the Anthropic Sonnet family.
 */
function nonTextVendorCode(normalized: string): string | null {
  const m = normalized.match(
    /^(black-forest-labs|bytedance-seed|bytedance|alibaba|elevenlabs|amazon|voyageai|2captcha|fal-ai|typesafe|microsoft)\/(.+)$/,
  );
  if (!m) return null;
  const vendor = m[1];
  const rest = m[2];
  const version = (rest.match(/(\d+(?:[-.]\d+)?)/) || [])[1]?.replace(/-/g, '.') || '';
  switch (vendor) {
    case 'black-forest-labs':
      return version ? `beacon-${version}` : 'beacon';
    case 'bytedance':
      return version ? `cipher-${version}` : 'cipher';
    case 'alibaba': {
      // TWO distinct Alibaba video lines ride this family (Qwen keeps quill),
      // so each carries its own product key. Keyed on the bare version alone,
      // a future wan1.1 and happy-horse v1.1 would mint the SAME codename —
      // the borrowed-codename defect this file exists to prevent. Never
      // hardcode a version here either: that pins every future build of a
      // line onto the pinned build's generation word.
      // Tags are OPAQUE SINGLE LETTERS, not the vendor's product names: the
      // 2026-07-07 rule bans vendor model designations from codenames, and
      // these codes reach persisted rows and logs. Same treatment as the
      // GPT-5.6 builds (sol/luna/terra -> prism-5.6-s/-l/-t); the public mint
      // words stay non-congruent, so the letters decode to nothing.
      const line = /happy-horse/.test(rest) ? 'h' : /wan/.test(rest) ? 'w' : '';
      if (!line) return version ? `atlas-${version}` : 'atlas';
      return version ? `atlas-${line}-${version}` : `atlas-${line}`;
    }
    case 'elevenlabs':
      // The speech lines (2026-10-12, Tiers 25 and 26) carry their own product keys, version-aware
      // so a new build mints a code the ship gate fails until it is named. Anything else stays the
      // bare family word, version-aware on purpose: a future versioned build mints 'chorus-<v>',
      // which the ship gate then fails until it is given a mint word, rather than silently
      // rendering as the same bare 'chorus' as the current one.
      if (/^scribe/.test(rest)) return version ? `chorus-stt-${version}` : 'chorus-stt';
      if (/^eleven-v\d/.test(rest)) return version ? `chorus-tts-${version}${/-turbo/.test(rest) ? '-turbo' : ''}` : 'chorus-tts';
      return version ? `chorus-${version}` : 'chorus';
    case 'amazon':
      return version ? `arbor-${version}` : 'arbor';
    case 'voyageai':
      return version ? `vantage-${version}${/large/.test(rest) ? '-large' : ''}` : 'vantage';
    case '2captcha':
      return 'sentry';
    case 'fal-ai': {
      // fal hosts several third-party audio lines under one prefix; each keeps
      // its own opaque line letter (the Alibaba precedent) so two lines at the
      // same version never mint the same codename.
      // mmaudio has two routes at one version: text-to-audio (m) and the video-input route (v).
      const line = /stable-audio/.test(rest) ? 's' : /mmaudio/.test(rest) ? (/text-to-audio/.test(rest) ? 'm' : 'v') : '';
      if (!line) return version ? `harbor-${version}` : 'harbor';
      return version ? `harbor-${line}-${version}` : `harbor-${line}`;
    }
    case 'typesafe':
      // System One decision models (Tier 23, 2026-09-28). Version-aware, as chorus:
      // a new build mints 'reflex-<v>', which the ship gate fails until it has a mint word.
      return version ? `reflex-${version}` : 'reflex';
    case 'microsoft': {
      // The speech lines (Tiers 25 and 26, 2026-10-12) carry their own product keys inside the family.
      if (/mai-transcribe/.test(rest)) return version ? `mosaic-transcribe-${version}` : 'mosaic-transcribe';
      if (/mai-voice/.test(rest)) return version ? `mosaic-voice-${version}${/-flash/.test(rest) ? '-flash' : ''}` : 'mosaic-voice';
      // Otherwise the MAI-Image line only (Tier 8, 2026-10-12): another Microsoft line is not an
      // image build and must not take this family's words.
      if (!/mai-image/.test(rest)) return null;
      const build = /-flash/.test(rest) ? '-flash' : /-pro/.test(rest) ? '-pro' : '';
      return version ? `mosaic-${version}${build}` : 'mosaic';
    }
    case 'bytedance-seed': {
      // ByteDance's image product through the model gateway (Seedream, Tier 8, 2026-10-12): one
      // product key inside the cipher family, version-aware so a new build never inherits 5.0's code.
      const build = /-pro/.test(rest) ? '-pro' : /-lite/.test(rest) ? '-lite' : /-flash/.test(rest) ? '-flash' : '';
      return version ? `cipher-image-${version}${build}` : 'cipher-image';
    }
    default:
      return null;
  }
}

export const getModelCodeName = (model?: string): string => {
  if (!model) return 'unknown';

  const raw = String(model).trim();
  // R17-P1 (WS-A2/A3): a '+workflow-intent' suffix marks the zero-cost
  // deterministic-dispatch ledger row (no model call was made — Prest0n itself
  // dispatched the workflow). Render it as the neutral prest0n station rather
  // than attributing a model that never ran.
  if (raw.toLowerCase().endsWith('+workflow-intent')) return 'prest0n-routed';
  let normalized = raw
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-');

  // Strip provider prefix(es) iteratively, including the `~` always-latest
  // sigil (e.g. `openrouter/~anthropic/claude-opus-latest`). Mirrors
  // normalizeModelKey in aiModelPricing.ts — keep the two in sync.
  for (let i = 0; i < 4; i += 1) {
    const next = normalized.replace(/^~?(openrouter|anthropic|openai|google|perplexity|x-ai|z-ai|groq|deepseek|qwen|moonshotai|minimax|xiaomi|meta-llama|meta)\//i, '');
    if (next === normalized) break;
    normalized = next;
  }

  // Return mapped code name if available
  if (MODEL_CODE_NAMES[normalized]) {
    return MODEL_CODE_NAMES[normalized];
  }

  // Non-text product vendors (2026-09-02) — resolved from the raw vendor
  // prefix, before any version/family regex below can mis-claim the id.
  const vendorCode = nonTextVendorCode(normalized);
  if (vendorCode) return vendorCode;

  // Regex-based normalization so UI never falls back to vendor model names
  const versionMatch = normalized.match(/(\d+(?:[-.]\d+)?)/);
  const version = versionMatch ? versionMatch[1].replace(/-/g, '.') : '';
  const majorVersion = version ? parseInt(version, 10) : NaN;

  // Fable BEFORE the opus/sonnet branches: 'claude-fable-5' must resolve to the
  // Apogee family, never fall through to an apex-<version> guess.
  if (/claude-fable|\bfable\b/i.test(normalized)) {
    return version ? `apogee-${version}` : 'apogee';
  }
  if (/claude-opus|\bopus\b/i.test(normalized)) {
    return version ? `apex-${version}` : 'apex';
  }
  if (/claude-sonnet|\bsonnet\b/i.test(normalized)) {
    // Nova revived for Sonnet majors >= 5 (2026-07-02 sanction); legacy
    // majors <= 4 keep the 2026-05-10 Apex-4.7 retirement remap.
    if (!Number.isNaN(majorVersion) && majorVersion >= 5) return `nova-${version}`;
    return 'apex-4.7';
  }
  if (/claude-haiku|\bhaiku\b/i.test(normalized)) {
    // Swift retired 2026-05-10; legacy majors <= 4 remap to Apex-4.7. A
    // future re-sanctioned Haiku (major >= 5) resolves to its own family.
    if (!Number.isNaN(majorVersion) && majorVersion >= 5) return `swift-${version}`;
    return 'apex-4.7';
  }
  if (/sonar-deep-research/i.test(normalized)) return 'echo-deep';
  if (/sonar-reasoning-pro/i.test(normalized)) return 'echo-reasoning-pro';
  if (/sonar-reasoning/i.test(normalized)) return 'echo-reasoning';
  if (/sonar-pro/i.test(normalized)) return 'echo-pro';
  if (/\bsonar\b/i.test(normalized)) return 'echo';
  if (/gpt-(\d+(?:[-.]\d+)?)-codex/i.test(normalized)) {
    // 2026-07-07 (Miles directive): vendor model designations ('codex') must
    // not leak into the codename — the plain versioned family name is used.
    return version ? `prism-${version}` : 'prism';
  }
  // GPT-5.6 generation (2026-07-10): Sol/Luna/Terra are variant names (like
  // Mini/Nano), kept in the codename; -pro serving mode collapses to the base.
  // GPT-6 generation (2026-09-04): Astra is a variant name (like Sol/Luna/Terra) -> tag 'a'.
  if (/gpt-6-astra/i.test(normalized)) return 'prism-6-a';
  // 2026-09-30: the rest of the GPT-6 generation (the -pro serving mode collapses to the base).
  if (/gpt-6\.1-sol/i.test(normalized)) return 'prism-6.1-s';
  if (/gpt-6-sol/i.test(normalized)) return 'prism-6-s';
  if (/gpt-6-luna/i.test(normalized)) return 'prism-6-l';
  if (/gpt-5\.6-sol/i.test(normalized)) return 'prism-5.6-s';
  if (/gpt-5\.6-luna/i.test(normalized)) return 'prism-5.6-l';
  if (/gpt-5\.6-terra/i.test(normalized)) return 'prism-5.6-t';
  if (/gpt-5\.4-mini/i.test(normalized)) return 'prism-5.4-mini';
  if (/gpt-5\.4-nano/i.test(normalized)) return 'prism-5.4-nano';
  if (/gpt-5\.4/i.test(normalized)) return 'prism-5.4';
  if (/gpt-mini/i.test(normalized)) return 'prism-mini';
  if (/gpt-latest/i.test(normalized)) return 'prism-5.6-s'; // retargeted 2026-07-10
  if (/gpt-oss/i.test(normalized)) return 'prism-oss';
  if (/gpt-4-turbo/i.test(normalized)) return 'prism-4t';
  if (/gpt-4/i.test(normalized)) return 'prism-4';
  if (/gpt-3\.5/i.test(normalized)) return 'prism-3';
  if (/\bgpt\b|gpt-/i.test(normalized)) {
    return version ? `prism-${version}` : 'prism';
  }
  if (/gemini-pro-latest/i.test(normalized)) return 'titan-pro';
  if (/gemini-3\.1-pro/i.test(normalized)) return 'titan-3.1';
  if (/gemini-(\d+(?:[-.]\d+)?)-flash/i.test(normalized)) {
    return version ? `titan-${version}-flash` : 'titan-flash';
  }
  if (/gemini/i.test(normalized)) {
    return version ? `titan-${version}` : 'titan';
  }
  if (/grok/i.test(normalized)) {
    return version ? `pulse-${version}` : 'pulse';
  }
  if (/\bglm\b|glm-/i.test(normalized)) {
    return version ? `zenith-${version}` : 'zenith';
  }
  if (/minimax/i.test(normalized)) {
    return version ? `maxim-${version}` : 'maxim';
  }
  if (/deepseek/i.test(normalized)) {
    const variant = /flash/i.test(normalized) ? '-flash' : /pro/i.test(normalized) ? '-pro' : '';
    return version ? `fathom-${version}${variant}` : 'fathom';
  }
  if (/kimi/i.test(normalized)) {
    const variant = /-code\b/i.test(normalized) ? '-code' : '';
    return version ? `lunar-${version}${variant}` : 'lunar';
  }
  if (/mimo/i.test(normalized)) {
    const variant = /pro/i.test(normalized) ? '-pro' : '';
    return version ? `mirage-${version}${variant}` : 'mirage';
  }
  if (/qwen/i.test(normalized)) {
    const variant = /max/i.test(normalized) ? '-max' : '';
    return version ? `quill-${version}${variant}` : 'quill';
  }
  if (/muse-spark|\bmuse\b/i.test(normalized)) {
    // Meta Muse Spark -> Motif family (minted 2026-08-07). Guarded BEFORE the
    // codename pass-through: legacy 'spark' there is a retired Gemini family
    // and must never absorb Meta ids.
    return version ? `motif-${version}` : 'motif';
  }

  // Already a codename? (Persisted records / VM bridges can round-trip
  // codenames back through this resolver.) Pass it through unchanged.
  if (/^(apex|nova|apogee|swift|echo|prism|titan|spark|pulse|zenith|maxim|fathom|lunar|mirage|quill|motif|beacon|cipher|atlas|chorus|arbor|vantage|sentry|harbor|reflex|mosaic)(-|$)/i.test(normalized)) {
    return normalized.replace(/-20\d{6}$/, '').trim();
  }

  // White-label mandate: never leak an unrecognized vendor id to the UI.
  // Callers that know the provider should use getModelCodeNameForUsage,
  // which falls back to the provider's codename family.
  return 'unknown';
};

/**
 * Usage-row codename resolver for the ⓘ cost popout (2026-06-11 backlog
 * item 1: "model code names are not showing up (or are absent)").
 *
 * `getModelCodeName` returns the literal string 'unknown' when the usage
 * record carries no model id — which was every server-driven prest0n-session
 * turn until the backend started populating `usage.model`, and remains true
 * for any historical message persisted without one. When the model id is
 * missing but the provider is known, fall back to the provider's codename
 * family so the popup always shows a meaningful name instead of 'unknown'.
 */
export const getModelCodeNameForUsage = (model?: string, provider?: string): string => {
  const fromModel = getModelCodeName(model);
  if (fromModel !== 'unknown') return fromModel;

  const key = (provider || '').toLowerCase();
  if (key === 'openrouter') {
    // Turn went through our router but the model id was not reported —
    // white-label neutral label (no family information available).
    return 'prest0n-routed';
  }
  // Single source: the provider table above (was a parallel switch that had
  // already drifted from it — e.g. it knew 'xai' while the table did not).
  const family = PROVIDER_CODE_NAMES[key];
  return family && family !== 'system' ? family.toLowerCase() : 'unknown';
};

/**
 * Derive the provider family from a model id (2026-07-06). Server-driven
 * prest0n turns historically hard-coded provider 'openrouter' or 'claude'
 * regardless of which ladder rung actually served the call — this helper
 * lets every recording/display path stamp the real family so provider dots,
 * filters, and codename fallbacks stay accurate.
 */
export const detectProviderFromModelId = (model?: string): string => {
  if (!model) return 'unknown';
  if (String(model).trim().toLowerCase().startsWith('app:')) return 'composio';
  const codeName = getModelCodeName(model);
  const family = codeName.split('-')[0];
  const fromFamily = PROVIDER_FROM_CODE_NAME[family];
  if (fromFamily && fromFamily !== 'unknown') return fromFamily;
  // R17-F (2026-08-15 alias-drift incident): a served id with no codename row —
  // exactly what floating-alias drift produces — must still color its provider
  // dot. Fall back to the raw vendor prefix when it is a known provider key.
  const vendor = String(model)
    .trim()
    .toLowerCase()
    .replace(/^openrouter\//, '')
    .replace(/^~/, '')
    .split('/')[0];
  if (PROVIDER_COLORS[vendor]) return vendor;
  return 'unknown';
};
